/**
 * Unified persistent tile cache with stale-while-revalidate semantics and
 * LRU eviction over a budget.
 *
 * Rules (from project memory):
 *   - Always return cached data immediately if present.
 *   - Background-revalidate past TTL — never evict on TTL alone.
 *   - Honor Cache-Control max-age and ETag where servers provide them.
 *   - Pinned tiles (from explicit plan downloads) are never auto-evicted.
 *   - Unpinned tiles are LRU-evicted only when the in-memory budget is exceeded.
 *
 * This is the canonical pattern from weather-service.ts and avalanche-service.ts,
 * adapted for tiles.
 */

import { db } from '../../database/schema.js'
import type {
  CachedTile,
  CanFitResult,
  PinResult,
  TileCacheStats,
  TileCoord,
} from './tile-cache-types.js'

/** Default TTL when a layer config doesn't override and the server sends no Cache-Control. */
const DEFAULT_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

/** Budget recompute interval — picks up navigator.storage.estimate() changes. */
const BUDGET_REFRESH_MS = 5 * 60 * 1000 // 5 minutes

/** Eviction target headroom — evict down to this fraction of budget to avoid thrash. */
const EVICT_TARGET_FRACTION = 0.9

/** Eviction debounce — coalesce rapid writes into one pass. */
const EVICT_DEBOUNCE_MS = 500

/** lastAccessedAt bump debounce — don't write on every tile render during a pan. */
const ACCESS_BUMP_INTERVAL_MS = 60 * 1000

/** Clamps for the auto-computed budget. */
const BUDGET_MIN_BYTES = 250 * 1024 * 1024 //  250 MB
const BUDGET_MAX_BYTES = 4 * 1024 * 1024 * 1024 //   4 GB
const BUDGET_FRACTION_OF_QUOTA = 0.25

/** Fallback quota estimate if navigator.storage.estimate() is unavailable. */
const FALLBACK_QUOTA_BYTES = 2 * 1024 * 1024 * 1024 // 2 GB

/** Ceiling for "how full can we get" before the download pre-flight refuses. */
const CAN_FIT_CEILING_FRACTION = 0.8

/** How many concurrent network fetches per pin batch. */
const PIN_CONCURRENCY = 6

export interface TileFetchOptions {
  /** Layer-default TTL in ms, used when server sends no Cache-Control max-age. */
  defaultTtlMs?: number
  /** Abort in-flight network fetches. Does not affect cache hits. */
  signal?: AbortSignal
}

export class TileCacheService {
  /** In-memory running counter of unpinned bytes. Reconciled from Dexie at init. */
  private unpinnedBytes = 0
  /** In-memory running counter of pinned bytes. Reconciled from Dexie at init. */
  private pinnedBytes = 0
  /** Current budget for unpinned tiles. */
  private budgetBytes = BUDGET_MIN_BYTES
  /** Last navigator.storage.estimate() quota, if available. */
  private deviceQuota: number | null = null
  /** Last navigator.storage.estimate() usage, if available. */
  private deviceUsage: number | null = null

  private initPromise: Promise<void> | null = null
  private evictTimer: ReturnType<typeof setTimeout> | null = null
  private evictInFlight = false
  /** key → lastAccessedAt we last wrote. Used to debounce access bumps. */
  private lastAccessBump = new Map<string, number>()

  /** User override (bytes). If set, bypasses the auto-computed budget. */
  private userBudgetOverride: number | null = null

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise
    this.initPromise = this.doInit()
    return this.initPromise
  }

  private async doInit(): Promise<void> {
    // Reconcile byte counters from Dexie. Sum by pinned flag.
    let unpinned = 0
    let pinned = 0
    await db.tileCache.each(tile => {
      if (tile.pinned) pinned += tile.byteSize
      else unpinned += tile.byteSize
    })
    this.unpinnedBytes = unpinned
    this.pinnedBytes = pinned

    await this.refreshBudget()
    setInterval(() => {
      void this.refreshBudget()
    }, BUDGET_REFRESH_MS)
  }

  /**
   * Override the auto budget (e.g. from user settings). Pass null to clear.
   */
  setBudgetOverride(bytes: number | null): void {
    this.userBudgetOverride = bytes
    void this.refreshBudget()
  }

  private async refreshBudget(): Promise<void> {
    if (this.userBudgetOverride != null) {
      this.budgetBytes = this.userBudgetOverride
    } else {
      let quota = FALLBACK_QUOTA_BYTES
      try {
        const est = await navigator.storage?.estimate?.()
        if (est) {
          this.deviceQuota = est.quota ?? null
          this.deviceUsage = est.usage ?? null
          if (est.quota) quota = est.quota
        }
      } catch {
        // Ignore — fall through to FALLBACK_QUOTA_BYTES.
      }
      const auto = Math.floor(quota * BUDGET_FRACTION_OF_QUOTA)
      this.budgetBytes = Math.max(BUDGET_MIN_BYTES, Math.min(BUDGET_MAX_BYTES, auto))
    }
  }

  /**
   * SWR fetch: return cached blob immediately if present, fire background
   * revalidation past expiresAt, otherwise fetch + persist + return.
   *
   * Returns null only if uncached AND fetch fails (e.g. offline miss).
   */
  async getTile(
    sourceId: string,
    url: string,
    z: number,
    x: number,
    y: number,
    options: TileFetchOptions = {}
  ): Promise<Blob | null> {
    await this.init()
    const key = `${sourceId}/${z}/${x}/${y}`
    const cached = await db.tileCache.get(key)

    if (cached) {
      // Debounced LRU bump.
      this.bumpAccess(key)

      // Trigger background revalidation if past TTL and not pinned.
      if (cached.expiresAt != null && Date.now() > cached.expiresAt && !cached.pinned) {
        void this.revalidateInBackground(cached, url, options.defaultTtlMs)
      }

      return cached.blob
    }

    // Miss — blocking fetch.
    const fetched = await this.fetchTile(url, null, options.defaultTtlMs, options.signal)
    if (!fetched) return null

    const tile: CachedTile = {
      key,
      sourceId,
      z,
      x,
      y,
      blob: fetched.blob,
      contentType: fetched.contentType,
      etag: fetched.etag,
      fetchedAt: Date.now(),
      lastAccessedAt: Date.now(),
      expiresAt: fetched.expiresAt,
      pinned: 0,
      byteSize: fetched.blob.size,
    }
    await db.tileCache.put(tile)

    this.unpinnedBytes += tile.byteSize
    if (this.unpinnedBytes > this.budgetBytes) this.scheduleEviction()

    return tile.blob
  }

  private bumpAccess(key: string): void {
    const now = Date.now()
    const last = this.lastAccessBump.get(key) ?? 0
    if (now - last < ACCESS_BUMP_INTERVAL_MS) return
    this.lastAccessBump.set(key, now)
    // Fire-and-forget — not critical if it fails.
    void db.tileCache.update(key, { lastAccessedAt: now })
  }

  private async revalidateInBackground(
    cached: CachedTile,
    url: string,
    defaultTtlMs: number | undefined
  ): Promise<void> {
    try {
      const fetched = await this.fetchTile(url, cached.etag, defaultTtlMs)
      if (!fetched) return

      if (fetched.notModified) {
        // 304 — bump freshness, keep blob.
        await db.tileCache.update(cached.key, {
          fetchedAt: Date.now(),
          expiresAt: fetched.expiresAt,
        })
        return
      }

      // 200 — replace blob.
      const delta = fetched.blob.size - cached.byteSize
      await db.tileCache.update(cached.key, {
        blob: fetched.blob,
        contentType: fetched.contentType,
        etag: fetched.etag,
        fetchedAt: Date.now(),
        expiresAt: fetched.expiresAt,
        byteSize: fetched.blob.size,
      })
      this.unpinnedBytes += delta
      if (this.unpinnedBytes > this.budgetBytes) this.scheduleEviction()
    } catch {
      // Silently fail — stale data remains available.
    }
  }

  /**
   * Low-level fetch. Parses Cache-Control and ETag, returns a normalized shape.
   * Handles 304 Not Modified when an ETag is supplied.
   */
  private async fetchTile(
    url: string,
    etag: string | null,
    defaultTtlMs: number | undefined,
    signal?: AbortSignal
  ): Promise<{
    blob: Blob
    contentType: string
    etag: string | null
    expiresAt: number | null
    notModified: boolean
  } | null> {
    try {
      const headers: Record<string, string> = {}
      if (etag) headers['If-None-Match'] = etag
      const response = await fetch(url, signal ? { headers, signal } : { headers })

      if (response.status === 304) {
        return {
          blob: new Blob(),
          contentType: '',
          etag,
          expiresAt: computeExpiresAt(response.headers.get('cache-control'), defaultTtlMs),
          notModified: true,
        }
      }
      if (!response.ok) return null

      const blob = await response.blob()
      return {
        blob,
        contentType: response.headers.get('content-type') ?? 'application/octet-stream',
        etag: response.headers.get('etag'),
        expiresAt: computeExpiresAt(response.headers.get('cache-control'), defaultTtlMs),
        notModified: false,
      }
    } catch {
      return null
    }
  }

  /**
   * Bulk download: fetch + persist with pinned=1. Used by the download workflow.
   * Cache hits are pinned in place. Failures are counted but don't abort.
   */
  async pinTiles(
    sourceId: string,
    tiles: readonly TileCoord[],
    urlBuilder: (z: number, x: number, y: number) => string,
    onProgress: (done: number, total: number) => void,
    signal: AbortSignal,
    options: TileFetchOptions = {}
  ): Promise<PinResult> {
    await this.init()
    let ok = 0
    let failed = 0
    let bytes = 0
    let done = 0

    // Work queue — pull from the front, with PIN_CONCURRENCY workers.
    let cursor = 0
    const worker = async (): Promise<void> => {
      while (cursor < tiles.length) {
        if (signal.aborted) return
        const index = cursor++
        const tile = tiles[index]
        const result = await this.pinOne(sourceId, tile, urlBuilder, options)
        if (result.ok) {
          ok++
          bytes += result.bytes
        } else {
          failed++
        }
        done++
        onProgress(done, tiles.length)
      }
    }

    const workers: Promise<void>[] = []
    for (let i = 0; i < PIN_CONCURRENCY; i++) workers.push(worker())
    await Promise.all(workers)

    return { ok, failed, bytes }
  }

  private async pinOne(
    sourceId: string,
    { z, x, y }: TileCoord,
    urlBuilder: (z: number, x: number, y: number) => string,
    options: TileFetchOptions
  ): Promise<{ ok: boolean; bytes: number }> {
    const key = `${sourceId}/${z}/${x}/${y}`
    const existing = await db.tileCache.get(key)
    if (existing) {
      // Already have it — just pin it if it wasn't.
      if (!existing.pinned) {
        await db.tileCache.update(key, { pinned: 1 })
        this.unpinnedBytes -= existing.byteSize
        this.pinnedBytes += existing.byteSize
      }
      return { ok: true, bytes: existing.byteSize }
    }

    const url = urlBuilder(z, x, y)
    const fetched = await this.fetchTile(url, null, options.defaultTtlMs)
    if (!fetched) return { ok: false, bytes: 0 }

    const tile: CachedTile = {
      key,
      sourceId,
      z,
      x,
      y,
      blob: fetched.blob,
      contentType: fetched.contentType,
      etag: fetched.etag,
      fetchedAt: Date.now(),
      lastAccessedAt: Date.now(),
      expiresAt: fetched.expiresAt,
      pinned: 1,
      byteSize: fetched.blob.size,
    }
    await db.tileCache.put(tile)
    this.pinnedBytes += tile.byteSize
    return { ok: true, bytes: tile.byteSize }
  }

  /** Store a raw tile blob directly (no HTTP fetch). Used for PMTiles extraction. */
  async putTileBlob(
    sourceId: string,
    z: number,
    x: number,
    y: number,
    data: ArrayBuffer,
    contentType: string
  ): Promise<number> {
    await this.init()
    const key = `${sourceId}/${z}/${x}/${y}`
    const existing = await db.tileCache.get(key)
    if (existing) {
      if (!existing.pinned) {
        await db.tileCache.update(key, { pinned: 1 })
        this.unpinnedBytes -= existing.byteSize
        this.pinnedBytes += existing.byteSize
      }
      return existing.byteSize
    }
    const blob = new Blob([data], { type: contentType })
    const tile: CachedTile = {
      key,
      sourceId,
      z,
      x,
      y,
      blob,
      contentType,
      etag: null,
      fetchedAt: Date.now(),
      lastAccessedAt: Date.now(),
      expiresAt: null,
      pinned: 1,
      byteSize: blob.size,
    }
    await db.tileCache.put(tile)
    this.pinnedBytes += tile.byteSize
    return tile.byteSize
  }

  /** Unpin all tiles for a source, making them eligible for LRU eviction. */
  async unpinSource(sourceId: string): Promise<void> {
    await this.init()
    const rows = await db.tileCache.where('sourceId').equals(sourceId).toArray()
    for (const row of rows) {
      if (row.pinned) {
        await db.tileCache.update(row.key, { pinned: 0 })
        this.pinnedBytes -= row.byteSize
        this.unpinnedBytes += row.byteSize
      }
    }
  }

  /**
   * SWR fetch for non-tile resources (style JSON, TileJSON, glyphs, etc.).
   * Same cache-first semantics as getTile but keyed by an arbitrary string.
   */
  async getResource(
    cacheKey: string,
    url: string,
    options: TileFetchOptions = {}
  ): Promise<Blob | null> {
    await this.init()
    const key = `_resource/${cacheKey}`
    const cached = await db.tileCache.get(key)

    if (cached) {
      this.bumpAccess(key)
      if (cached.expiresAt != null && Date.now() > cached.expiresAt && !cached.pinned) {
        void this.revalidateInBackground(cached, url, options.defaultTtlMs)
      }
      return cached.blob
    }

    const fetched = await this.fetchTile(url, null, options.defaultTtlMs, options.signal)
    if (!fetched) return null

    const tile: CachedTile = {
      key,
      sourceId: '_resource',
      z: 0,
      x: 0,
      y: 0,
      blob: fetched.blob,
      contentType: fetched.contentType,
      etag: fetched.etag,
      fetchedAt: Date.now(),
      lastAccessedAt: Date.now(),
      expiresAt: fetched.expiresAt,
      pinned: 0,
      byteSize: fetched.blob.size,
    }
    await db.tileCache.put(tile)

    this.unpinnedBytes += tile.byteSize
    if (this.unpinnedBytes > this.budgetBytes) this.scheduleEviction()

    return tile.blob
  }

  /** Pin a single resource by URL. Used by the download workflow for style metadata. */
  async pinResource(cacheKey: string, url: string): Promise<{ ok: boolean; bytes: number }> {
    await this.init()
    const key = `_resource/${cacheKey}`
    const existing = await db.tileCache.get(key)
    if (existing) {
      if (!existing.pinned) {
        await db.tileCache.update(key, { pinned: 1 })
        this.unpinnedBytes -= existing.byteSize
        this.pinnedBytes += existing.byteSize
      }
      return { ok: true, bytes: existing.byteSize }
    }

    const fetched = await this.fetchTile(url, null, undefined)
    if (!fetched) return { ok: false, bytes: 0 }

    const tile: CachedTile = {
      key,
      sourceId: '_resource',
      z: 0,
      x: 0,
      y: 0,
      blob: fetched.blob,
      contentType: fetched.contentType,
      etag: fetched.etag,
      fetchedAt: Date.now(),
      lastAccessedAt: Date.now(),
      expiresAt: fetched.expiresAt,
      pinned: 1,
      byteSize: fetched.blob.size,
    }
    await db.tileCache.put(tile)
    this.pinnedBytes += tile.byteSize
    return { ok: true, bytes: tile.byteSize }
  }

  /** Bulk delete all unpinned tiles. Used by storage management UI. */
  async clearUnpinned(): Promise<number> {
    await this.init()
    const keys = await db.tileCache.where('pinned').equals(0).primaryKeys()
    if (keys.length > 0) {
      await db.tileCache.bulkDelete(keys)
      this.unpinnedBytes = 0
      this.lastAccessBump.clear()
    }
    return keys.length
  }

  async getStorageStats(): Promise<TileCacheStats> {
    await this.init()
    const perSource = new Map<string, number>()
    await db.tileCache.each(tile => {
      perSource.set(tile.sourceId, (perSource.get(tile.sourceId) ?? 0) + tile.byteSize)
    })
    return {
      unpinnedBytes: this.unpinnedBytes,
      pinnedBytes: this.pinnedBytes,
      perSource,
      budgetBytes: this.budgetBytes,
      deviceQuota: this.deviceQuota,
      deviceUsage: this.deviceUsage,
    }
  }

  /**
   * Pre-flight check for the download workflow. Refuses if adding the
   * requested bytes would push total device usage past 80% of quota.
   */
  async canFit(additionalBytes: number): Promise<CanFitResult> {
    await this.init()
    await this.refreshBudget()

    if (this.deviceQuota == null || this.deviceUsage == null) {
      // No information — allow, but signal unknown.
      return {
        ok: true,
        availableBytes: Number.POSITIVE_INFINITY,
        deviceQuota: this.deviceQuota,
        deviceUsage: this.deviceUsage,
      }
    }

    const ceiling = Math.floor(this.deviceQuota * CAN_FIT_CEILING_FRACTION)
    const available = Math.max(0, ceiling - this.deviceUsage)
    return {
      ok: additionalBytes <= available,
      availableBytes: available,
      deviceQuota: this.deviceQuota,
      deviceUsage: this.deviceUsage,
    }
  }

  /**
   * Evict unpinned tiles (LRU by lastAccessedAt) until unpinnedBytes <= targetBytes.
   * Never deletes pinned tiles.
   */
  async evictLRU(targetBytes: number): Promise<{ deleted: number; freedBytes: number }> {
    await this.init()
    if (this.unpinnedBytes <= targetBytes) return { deleted: 0, freedBytes: 0 }

    // Query unpinned tiles ordered by lastAccessedAt ascending.
    const unpinned = await db.tileCache.where('pinned').equals(0).toArray()
    unpinned.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt)

    let deleted = 0
    let freed = 0
    const toDelete: string[] = []
    for (const tile of unpinned) {
      if (this.unpinnedBytes - freed <= targetBytes) break
      toDelete.push(tile.key)
      freed += tile.byteSize
      deleted++
    }

    if (toDelete.length > 0) {
      await db.tileCache.bulkDelete(toDelete)
      this.unpinnedBytes -= freed
      for (const key of toDelete) this.lastAccessBump.delete(key)
    }

    return { deleted, freedBytes: freed }
  }

  private scheduleEviction(): void {
    if (this.evictInFlight) return
    if (this.evictTimer != null) return
    this.evictTimer = setTimeout(() => {
      this.evictTimer = null
      void this.runEviction()
    }, EVICT_DEBOUNCE_MS)
  }

  private async runEviction(): Promise<void> {
    if (this.evictInFlight) return
    this.evictInFlight = true
    try {
      const target = Math.floor(this.budgetBytes * EVICT_TARGET_FRACTION)
      await this.evictLRU(target)
    } finally {
      this.evictInFlight = false
    }
  }
}

/**
 * Parse Cache-Control max-age and produce an absolute expiresAt (ms epoch),
 * falling back to the provided default if missing.
 */
function computeExpiresAt(
  cacheControl: string | null,
  defaultTtlMs: number | undefined
): number | null {
  const parsed = parseCacheControl(cacheControl)
  const ttl = parsed.maxAge != null ? parsed.maxAge * 1000 : (defaultTtlMs ?? DEFAULT_CACHE_TTL_MS)
  if (ttl <= 0) return null
  return Date.now() + ttl
}

function parseCacheControl(headerValue: string | null): { maxAge: number | null } {
  if (!headerValue) return { maxAge: null }
  const m = /max-age\s*=\s*(\d+)/i.exec(headerValue)
  return { maxAge: m ? parseInt(m[1], 10) : null }
}

/** Singleton. */
export const tileCacheService = new TileCacheService()
